import React, { useMemo, useState } from 'react';
import { ShareRole } from '../../types';
import { createShareLink } from '../../services/sharing';
import { Copy, Loader2, Link as LinkIcon } from 'lucide-react';

interface ShareDialogProps {
  isOpen: boolean;
  projectId: string;
  ownerId: string;
  projectTitle: string;
  projectUpdatedAt: Date;
  onClose: () => void;
}

export function ShareDialog({
  isOpen,
  projectId,
  ownerId,
  projectTitle,
  projectUpdatedAt,
  onClose,
}: ShareDialogProps) {
  const [role, setRole] = useState<ShareRole>('read');
  const [linkId, setLinkId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = useMemo(() => {
    if (!linkId) return '';
    return `${window.location.origin}/share/${linkId}`;
  }, [linkId]);

  async function handleGenerateLink() {
    setLoading(true);
    try {
      const link = await createShareLink(
        projectId,
        ownerId,
        role,
        projectTitle,
        projectUpdatedAt
      );
      setLinkId(link.id);
      setCopied(false);
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center gap-2 mb-4">
          <LinkIcon className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-gray-900">Share project</h2>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-2">
          Link permissions
        </label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as ShareRole)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
        >
          <option value="read">Read-only</option>
          <option value="comment">Can comment</option>
          <option value="edit">Can edit</option>
        </select>

        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={handleGenerateLink}
            disabled={loading}
            className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 flex items-center gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {linkId ? 'Generate new link' : 'Generate link'}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={shareUrl}
            placeholder="Generate a share link"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <button
            type="button"
            onClick={handleCopy}
            disabled={!shareUrl}
            aria-label="Copy share link"
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            title="Copy link"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
        {copied && (
          <div className="text-xs text-green-600 mt-2">Copied to clipboard</div>
        )}

        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
